import {CellContent, TableConfig, TCTypeEnum, FilterRoleEnum, SequenceTypeEnum, FilterOverrideFunctionsMap} from 'ngx-satoris';
import {subDays, subWeeks, subMonths, subYears, format, startOfDay, endOfDay} from 'date-fns';
import {GlobalOperationType, RequestState, RequestSubState} from 'src/app/shared/models/request';
import {DocumentType} from 'src/app/shared/models/PASS-COM/user-document';

export enum TableRequestsEnum {
  LOCK = 'lock', // lock
  NATIONALITY = 'nationality', // indexed.Nationality || metadata.Nationality
  BATCH_ID = 'batchId', // batchId
  FIRST_NAME = 'firstName', // indexed.FirstName || metadata.FirstName
  LAST_NAME = 'lastName', // indexed.LastName || metadata.LastName
  FULLNAME = 'fullName', // indexed.FirstName + indexed.LastName || metadata.FirstName + metadata.LastName
  PASSPORT_NUMBER = 'passportNumber', // indexed.PassportNumber || metadata.PassportNumber
  PAYMENT_STATUS = 'paymentStatus', // state
  CREATION_DATE = 'creationDate', // createdAt
  PRICE = 'price', // currency + amountCents
  USAGE_AFTER = 'usageAfter', // usageAfter + usageAfterValid boolean
  USAGE_UNTIL = 'usageUntil', // usageUntil + usageUntilValid boolean
  TIMESLOT = 'timeslot', // timeslot
  DOCUMENT_TYPE = 'operationId',
  APPLICATION_ID = 'applicationId', // id
  EXPANDABLE_DATA = 'expandableData', // expandableData
  REGISTRATION_PORT_ID = 'registrationPortId',
  REQUEST_REASON = 'requestReason',
  PASSPORT_TYPE = 'passportType'
}

export enum TableRequestsFilterEnum {
  LOCK = 'lock',
  NATIONALITY = 'nationality',
  BATCH_ID = 'batchId',
  FIRST_NAME = 'firstName',
  LAST_NAME = 'lastName',
  PASSPORT_NUMBER = 'passportNumber',
  PAYMENT_STATUS = 'paymentStatus',
  CREATE_AT_START = 'createAtStart',
  CREATE_AT_END = 'createAtEnd',
  SINCE_DAYS = 'sinceDays',
  PRICE = 'price',
  USAGE_AFTER = 'usageAfter',
  TIMESLOT = 'timeslot',
  TIMESLOT_START = 'timeslotStart',
  TIMESLOT_END = 'timeslotEnd',
  OPERATION_ID = 'operationId',
  APPLICATION_ID = 'applicationId',
  REGISTRATION_PORT_ID = 'registrationPortId',
  REQUEST_REASON = 'requestReason',
  PASSPORT_TYPE = 'passportType'
}
export const TABLE_REQUESTS_PRE_FILTERS: [TableRequestsFilterEnum, string][] = [
  [TableRequestsFilterEnum.FIRST_NAME, 'mrzFirstName'],
  [TableRequestsFilterEnum.LAST_NAME, 'mrzLastName']
];

export const TABLE_REQUESTS_CONFIG: TableConfig = {
  tableId: 'table-requests',
  version: '1.1.9',
  rowsClickable: true,
  useFilterCheckbox: true,
  pagination: {
    serverPagination: true,
    itemsPerPage: [5, 10, 20, 50, 100, 200, 300],
    activeItemsPerPage: 20,
    enablePageDataCaching: true,
    preloadPages: 1
  },
  rowsExpandable: {
    active: false
  },
  menusDisplay: {
    isFilterPanelCollapsed: false,
    isColumnPanelCollapsed: true
  },
  table: {
    [TableRequestsEnum.EXPANDABLE_DATA]: {
      isExpandableData: true,
      alwaysVisibleAndFixed: true,
      visible: false,
      sortable: false,
      serverSorting: false,
      filterable: false,
      data: [] as CellContent[]
    },
    [TableRequestsEnum.LOCK]: {
      alwaysVisibleAndFixed: true,
      visible: true,
      sortable: false,
      serverSorting: false,
      filterable: false,
      data: [] as CellContent[],
      filter: [{
        type: TCTypeEnum.SELECT,
        role: FilterRoleEnum.SINGLE,
        serverFilter: true,
        filterId: TableRequestsFilterEnum.LOCK,
        selectOptions: [
          {value: 'LOCKED', name: 'Locked'},
          {value: 'UNLOCKED', name: 'Unlocked'}
        ]
      }]
    },
    [TableRequestsEnum.NATIONALITY]: {
      visible: true,
      hideIfEmpty: true,
      sortable: false,
      serverSorting: false,
      filterable: true,
      data: [] as CellContent[],
      filter: [{
        rowIndex: 6,
        type: TCTypeEnum.SELECT,
        role: FilterRoleEnum.SINGLE,
        serverFilter: true,
        filterId: TableRequestsFilterEnum.NATIONALITY,
        selectOptions: [
        ],
        validators: {
          minLength: 3,
          maxLength: 50
        },
        sequence: {
          type: SequenceTypeEnum.HAS_VALUE,
          parentFilterId: [TableRequestsFilterEnum.LAST_NAME]
        }
      }]
    },
    [TableRequestsEnum.BATCH_ID]: {
      visible: false,
      sortable: false,
      serverSorting: false,
      filterable: true,
      data: [] as CellContent[],
      filter: [{
        rowIndex: 3,
        type: TCTypeEnum.TEXT,
        role: FilterRoleEnum.SINGLE,
        serverFilter: true,
        filterId: TableRequestsFilterEnum.BATCH_ID,
        validators: {
          minLength: 3,
          maxLength: 50
        }
      }]
    },
    [TableRequestsEnum.FIRST_NAME]: {
      visible: true,
      hideIfEmpty: true,
      sortable: false,
      serverSorting: false,
      filterable: true,
      data: [] as CellContent[],
      filter: [{
        rowIndex: 4,
        type: TCTypeEnum.TEXT,
        role: FilterRoleEnum.SINGLE,
        serverFilter: true,
        filterId: TableRequestsFilterEnum.FIRST_NAME,
        validators: {
          minLength: 3,
          maxLength: 50
        }
      }]
    },
    [TableRequestsEnum.LAST_NAME]: {
      visible: true,
      hideIfEmpty: true,
      sortable: false,
      serverSorting: false,
      filterable: true,
      data: [] as CellContent[],
      filter: [{
        rowIndex: 5,
        type: TCTypeEnum.TEXT,
        role: FilterRoleEnum.SINGLE,
        serverFilter: true,
        filterId: TableRequestsFilterEnum.LAST_NAME,
        validators: {
          minLength: 3,
          maxLength: 50
        },
        sequence: {
          type: SequenceTypeEnum.HAS_VALUE,
          parentFilterId: TableRequestsFilterEnum.FIRST_NAME
        }
      }]
    },
    [TableRequestsEnum.FULLNAME]: {
      visible: false,
      sortable: false,
      serverSorting: false,
      filterable: false,
      data: [] as CellContent[],
      filter: [{
        type: TCTypeEnum.TEXT,
        role: FilterRoleEnum.SINGLE,
        serverFilter: true,
        filterId: TableRequestsFilterEnum.LAST_NAME,
        validators: {
          minLength: 3,
          maxLength: 50
        }
      }]
    },
    [TableRequestsEnum.DOCUMENT_TYPE]: {
      visible: true,
      sortable: false,
      serverSorting: false,
      filterable: true,
      data: [] as CellContent[],
      filter: [{
        rowIndex: 11,
        type: TCTypeEnum.SELECT,
        role: FilterRoleEnum.SINGLE,
        serverFilter: true,
        filterId: TableRequestsFilterEnum.OPERATION_ID
      }]
    },
    [TableRequestsEnum.PASSPORT_TYPE]: {
      visible: true,
      sortable: false,
      serverSorting: false,
      filterable: true,
      data: [] as CellContent[],
      filter: [{
        type: TCTypeEnum.MULTISELECT,
        role: FilterRoleEnum.SINGLE,
        serverFilter: true,
        filterId: TableRequestsFilterEnum.PASSPORT_TYPE,
        sequence: {
          type: SequenceTypeEnum.IS_VALUE,
          value: DocumentType.COMPASS,
          parentFilterId: TableRequestsFilterEnum.OPERATION_ID
        },
        selectOptions: []
      }]
    },
    [TableRequestsEnum.REQUEST_REASON]: {
      visible: true,
      sortable: false,
      serverSorting: false,
      filterable: true,
      data: [] as CellContent[],
      filter: [{
        type: TCTypeEnum.MULTISELECT,
        role: FilterRoleEnum.SINGLE,
        serverFilter: true,
        filterId: TableRequestsFilterEnum.REQUEST_REASON,
        sequence: {
          type: SequenceTypeEnum.IS_VALUES,
          value: Object.values(DocumentType),
          parentFilterId: TableRequestsFilterEnum.OPERATION_ID
        },
        selectOptions: []
      }]
    },
    [TableRequestsEnum.PAYMENT_STATUS]: {
      visible: true,
      sortable: false,
      serverSorting: false,
      filterable: true,
      data: [] as CellContent[],
      filter: [
        {
          type: TCTypeEnum.SELECT,
          role: FilterRoleEnum.SINGLE,
          serverFilter: true,
          filterId: TableRequestsFilterEnum.PAYMENT_STATUS,
          sequence: {
            type: SequenceTypeEnum.HAS_VALUE,
            parentFilterId: TableRequestsFilterEnum.OPERATION_ID
          }
        }]
    },
    [TableRequestsEnum.CREATION_DATE]: {
      visible: true,
      sortable: true,
      serverSorting: true,
      filterable: true,
      data: [] as CellContent[],
      filter: [
        {
          type: TCTypeEnum.DATE,
          role: FilterRoleEnum.DATE_FROM,
          serverFilter: true,
          filterId: TableRequestsFilterEnum.CREATE_AT_START,
          validators: {
            maxDate: new Date()
          },
          checkGroup: 1
        },
        {
          type: TCTypeEnum.DATE,
          role: FilterRoleEnum.DATE_TO,
          serverFilter: true,
          filterId: TableRequestsFilterEnum.CREATE_AT_END,
          validators: {
            maxDate: new Date()
          },
          checkGroup: 1
        },
        {
          type: TCTypeEnum.SELECT,
          role: FilterRoleEnum.SINGLE,
          serverFilter: false,
          filterId: TableRequestsFilterEnum.SINCE_DAYS,
          target: [TableRequestsFilterEnum.CREATE_AT_START, TableRequestsFilterEnum.CREATE_AT_END],
          checkGroup: 2
        }
      ],
      dateFormat: 'dd/MM/yyyy HH:mm'
    },
    [TableRequestsEnum.PRICE]: {
      visible: true,
      sortable: false,
      serverSorting: false,
      filterable: false,
      data: [] as CellContent[],
      filter: [{
        type: TCTypeEnum.NUMBER,
        role: FilterRoleEnum.SINGLE,
        serverFilter: true,
        filterId: TableRequestsFilterEnum.PRICE
      }]
    },
    [TableRequestsEnum.REGISTRATION_PORT_ID]: {
      visible: false,
      sortable: true,
      serverSorting: true,
      filterable: false,
      data: [] as CellContent[]
    },
    [TableRequestsEnum.USAGE_AFTER]: {
      visible: false,
      sortable: false,
      serverSorting: false,
      filterable: false,
      data: [] as CellContent[],
      filter: [{
        type: TCTypeEnum.DATE,
        role: FilterRoleEnum.SINGLE,
        serverFilter: true,
        filterId: TableRequestsFilterEnum.USAGE_AFTER
      }],
      dateFormat: 'dd/MM/yyyy HH:mm'
    },
    [TableRequestsEnum.TIMESLOT]: {
      visible: false,
      sortable: true,
      serverSorting: true,
      filterable: true,
      data: [] as CellContent[],
      filter: [
        {
          type: TCTypeEnum.DATE,
          role: FilterRoleEnum.DATE_FROM,
          serverFilter: true,
          filterId: TableRequestsFilterEnum.TIMESLOT_START,
          checkGroup: 1
        },
        {
          type: TCTypeEnum.DATE,
          role: FilterRoleEnum.DATE_TO,
          serverFilter: true,
          filterId: TableRequestsFilterEnum.TIMESLOT_END,
          checkGroup: 1
        }
      ],
      dateFormat: 'dd/MM/yyyy HH:mm'
    },
    [TableRequestsEnum.APPLICATION_ID]: {
      visible: true,
      sortable: false,
      serverSorting: false,
      filterable: true,
      data: [] as CellContent[],
      filter: [{
        rowIndex: 1,
        type: TCTypeEnum.TEXT,
        role: FilterRoleEnum.SINGLE,
        serverFilter: true,
        filterId: TableRequestsFilterEnum.APPLICATION_ID
      }]
    }
  }
};

export const ORIGINAL_TABLE_REQUESTS_CONFIG: TableConfig = JSON.parse(JSON.stringify(TABLE_REQUESTS_CONFIG));

export const filterOverrideFunctions: FilterOverrideFunctionsMap = {
  [TableRequestsFilterEnum.PAYMENT_STATUS]: (status) => {
    switch(status) {
    case `${RequestState.PAID_NOT_CONFIRMED}.${RequestSubState.REVISIONS_0}`:
    case `${RequestState.PAID_NOT_CONFIRMED}.${RequestSubState.REVISIONS_1}`:
    case `${RequestState.PAID_NOT_CONFIRMED}.${RequestSubState.REVISIONS_2}`:
      return {applicationType: GlobalOperationType.VISA};
    }
    return {applicationType: ''};
  },

  [TableRequestsFilterEnum.SINCE_DAYS]: (period: string | string[]) => {
    const now = new Date();
    const periodStr = Array.isArray(period) ? period[0] : period;

    if(!period) {
      return {
        createAtStart: '',
        createAtEnd: ''
      };
    }

    const fromDate = calculateDateFromPeriod(parsePeriodString(periodStr), now);

    return {
      createAtStart: format(startOfDay(fromDate), 'yyyy-MM-dd'),
      createAtEnd: format(endOfDay(now), 'yyyy-MM-dd')
    };
  }
};

const parsePeriodString = (value: string): Period => {
  const amount = parseInt(value.replace(/[dwmy]$/, ''), 10);
  const unit = value.slice(-1) as PeriodUnit;
  return {amount, unit};
};

const calculateDateFromPeriod = (period: Period, fromDate: Date = new Date()): Date => {
  switch(period.unit) {
  case 'd':
    return subDays(fromDate, period.amount);
  case 'w':
    return subWeeks(fromDate, period.amount);
  case 'm':
    return subMonths(fromDate, period.amount);
  case 'y':
    return subYears(fromDate, period.amount);
  default:
    return subDays(fromDate, period.amount);
  }
};

type PeriodUnit = 'd' | 'w' | 'm' | 'y';

interface Period {
  amount: number;
  unit: PeriodUnit;
}
